define([
  'react',
  'prop-types',
  'react-dom',
  'immutable',
  'underscore',
  'iframe-tracker',
  'es6!sumo/util',
  'jsx!launcher/views/listbuilder/wysiwyg/components/Component',
], (React, PropTypes, ReactDOM, Immutable, _, IFrameTracker, Util, Component) => {
  const VALID_STYLE_ATTRS = [ ];
  const DEFAULT_FACEBOOK_URL = 'https://www.facebook.com/sumo';
  const DEFAULT_TWITTER_HANDLE = 'sumome';
  const DEFAULT_PINTEREST_USERNAME = 'sumomeplease';
  const TWITTER_BASE_WIDTH = 210;
  const TWITTER_CHAR_WIDTH_MULTIPLIER = 7;

  @Component({ disableVerticalResize: true, disableHorizontalResize: true })
  class SocialButton extends React.Component {
    static propTypes = {
      id: PropTypes.string.isRequired,
      style: PropTypes.object,
    };

    static defaultProps = {
      style: Immutable.Map(),
    }

    constructor(props) {
      super(props);

      this.SOCIAL_RENDER_FUNCTIONS = {
        twitter: this.renderTwitterButton.bind(this),
        facebook: this.renderFacebookButton.bind(this),
        pinterest: this.renderPinterestButton.bind(this),
      };

      this.saveDimensions = this.saveDimensions.bind(this);
      this.onClickPinterest = this.onClickPinterest.bind(this);
    }

    componentDidMount() {
      const { onClickAction, socialChannel } = this.props;
      const componentEl = ReactDOM.findDOMNode(this);
      if (!componentEl) return;

      const iframe = componentEl.querySelector('iframe');
      if (!iframe) return;

      const iframeTracker = new IFrameTracker(iframe, {
        blurCallback: () => {
          if (onClickAction) {
            const focusHandler = () => {
              Util.removeEvent(window, 'focus', focusHandler);
              onClickAction('social', this);
            };

            Util.addEvent(window, 'focus', focusHandler);
          }
        },
      });
    }

    // Since the social button dimensions aren't controlled by us, we need to
    // save the dimensions after it gets rendered.
    saveDimensions(container) {
      if (!container) return;
      const { id, onChangeComponent, style, socialChannel } = this.props;

      const previousLeftPosition = parseInt(style.get('left'), 10);
      const previousWidth = parseInt(style.get('width'), 10);
      const centerPosition = Math.floor(previousLeftPosition + (previousWidth / 2));

      const newHeight = container.clientHeight;
      let newWidth = container.clientWidth;

      if (socialChannel === 'twitter') {
        const twitterHandle = this.props.twitterHandle || DEFAULT_TWITTER_HANDLE;
        newWidth = TWITTER_BASE_WIDTH + twitterHandle.length * TWITTER_CHAR_WIDTH_MULTIPLIER;
      }

      let newLeft = previousLeftPosition;
      if (previousLeftPosition > 80) {
        newLeft = Math.floor(centerPosition - newWidth / 2);
      }

      const newStyle = {
        width: `${newWidth}px`,
        height: `${newHeight}px`,
        left: `${newLeft}px`,
      };

      if (onChangeComponent) onChangeComponent(id, { style: newStyle }, true);
    }

    onClickPinterest() {
      const { onClickAction } = this.props;
      const pinterestUsername = this.props.pinterestUsername || DEFAULT_PINTEREST_USERNAME;

      if (onClickAction) onClickAction('social', this);

      const followURL = `https://www.pinterest.com/${pinterestUsername}/pins/follow/`;
      window.open(followURL, 'Follow', 'resizable,scrollbars,width=1040,height=640,left=0,top=0');
    }

    getSocialRenderFunction() {
      const { socialChannel } = this.props;
      // If no social render function exists, then return empty function.
      return this.SOCIAL_RENDER_FUNCTIONS[socialChannel] || (() => {});
    }

    renderFacebookButton() {
      const { facebookUrl } = this.props;
      const baseUrl = encodeURIComponent(facebookUrl || DEFAULT_FACEBOOK_URL);
      const iframeSrc = [
        `https://www.facebook.com/plugins/like.php?href=${baseUrl}`,
        'width=100',
        'height=20',
        'layout=button_count',
        'action=like',
        'show_faces=false',
        'share=false',
        'appId=859406404089021',
      ].join('&');

      return (
        <iframe
          key="facebook"
          ref={this.saveDimensions}
          src={iframeSrc}
          scrolling="no"
          frameBorder="0"
          allowTransparency="true"
          width="100"
          height="20"
          style={{ border: '0', overflow: 'hidden' }}
        />
      );
    }

    renderTwitterButton() {
      const twitterHandle = this.props.twitterHandle || DEFAULT_TWITTER_HANDLE;
      const width = TWITTER_BASE_WIDTH + twitterHandle.length * TWITTER_CHAR_WIDTH_MULTIPLIER;
      const iframeSrc = [
        `//platform.twitter.com/widgets/follow_button.html?screen_name=${twitterHandle}`,
        'show_screen_name=true',
        'show_count=true',
        'size=l',
      ].join('&');

      return (
        <iframe
          key="twitter"
          ref={this.saveDimensions}
          src={iframeSrc}
          title={`Follow ${twitterHandle} on Twitter`}
          scrolling="no"
          frameBorder="0"
          allowTransparency="true"
          width={`${width}`}
          height="30"
          style={{ border: '0', overflow: 'hidden' }}
        />
      );
    }

    renderPinterestButton() {
      const pinterestUsername = this.props.pinterestUsername || DEFAULT_PINTEREST_USERNAME;

      return (
        <span
          className="sumome-react-pinterest-follow-button"
          onClick={this.onClickPinterest}
          ref={this.saveDimensions}
        >
          {pinterestUsername}
        </span>
      );
    }

    render() {
      const { socialChannel, onMouseEnter, onMouseLeave } = this.props;
      const style = _.pick(this.props.style.toJS(), VALID_STYLE_ATTRS);
      const socialRenderFunction = this.getSocialRenderFunction();

      // We have to use dangerously set innerhtml since we want the user to have the
      // ability to use html in the text.
      return (
        <div
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {socialRenderFunction()}
        </div>
      );
    }
  }

  return SocialButton;
});
